import { STOPLIGHT_COLORS, BLACKS_AND_WHITES } from '../../utils/DataFormatterUtils';
import {
	COMPLIANCE_INSTANCE_STATUSES,
	COMPLIANCE_EXPIRY_STATUSES,
	COMPLIANCE_REQUEST_LIFECYCLES,
} from '../../types/ComplianceTypes';

// PostHog feature flag gating the entire Compliance Suite (the Compliance tab under
// Suppliers/Clients + the compliance routes). Off = the feature is hidden.
export const COMPLIANCE_FEATURE_FLAG = 'compliance';

// Display copy differs by role: the buyer waits on the supplier, the supplier
// sees the same status as a plain "Requested" (per Figma).
// 'Draft' is a pseudo-status on the buyer overview: draft requests fanned out
// to their targeted suppliers before publishing.
export const COMPLIANCE_INSTANCE_STATUS_DISPLAY_NAMES_BUYER = {
	[COMPLIANCE_REQUEST_LIFECYCLES.DRAFT]: 'Draft',
	[COMPLIANCE_INSTANCE_STATUSES.WAITING_ON_SUPPLIER]: 'Waiting on Supplier',
	[COMPLIANCE_INSTANCE_STATUSES.PENDING_REVIEW]: 'Pending Review',
	[COMPLIANCE_INSTANCE_STATUSES.APPROVED]: 'Approved',
	[COMPLIANCE_INSTANCE_STATUSES.REJECTED]: 'Rejected',
};

export const COMPLIANCE_INSTANCE_STATUS_DISPLAY_NAMES_SUPPLIER = {
	[COMPLIANCE_INSTANCE_STATUSES.WAITING_ON_SUPPLIER]: 'Requested',
	[COMPLIANCE_INSTANCE_STATUSES.PENDING_REVIEW]: 'Pending Review',
	[COMPLIANCE_INSTANCE_STATUSES.APPROVED]: 'Approved',
	[COMPLIANCE_INSTANCE_STATUSES.REJECTED]: 'Rejected',
};

export const COMPLIANCE_INSTANCE_STATUS_COLORS = {
	[COMPLIANCE_REQUEST_LIFECYCLES.DRAFT]: BLACKS_AND_WHITES.blackSecondary,
	[COMPLIANCE_INSTANCE_STATUSES.WAITING_ON_SUPPLIER]: STOPLIGHT_COLORS.yellowRed,
	[COMPLIANCE_INSTANCE_STATUSES.PENDING_REVIEW]: STOPLIGHT_COLORS.pureYellow,
	[COMPLIANCE_INSTANCE_STATUSES.APPROVED]: STOPLIGHT_COLORS.pureGreen,
	[COMPLIANCE_INSTANCE_STATUSES.REJECTED]: STOPLIGHT_COLORS.pureRed,
};

// Items for the shared chevron PipelineFilter (QuotesTable/pipelineFilters) on
// the buyer compliance Overview tab. `name` doubles as the `statuses` query
// value; counts come from request_instances count_by (Draft included as a
// pseudo-status).
export const BUYER_COMPLIANCE_PIPELINE_FILTER_ITEMS = [
	{
		displayName: 'Draft',
		name: COMPLIANCE_REQUEST_LIFECYCLES.DRAFT,
		icon: 'icons8-edit',
		iconStyle: { color: BLACKS_AND_WHITES.blackSecondary },
	},
	{
		displayName: 'Waiting on Supplier',
		name: COMPLIANCE_INSTANCE_STATUSES.WAITING_ON_SUPPLIER,
		icon: 'icons8-hourglass',
		iconStyle: { color: STOPLIGHT_COLORS.yellowRed },
	},
	{
		displayName: 'Pending Review',
		name: COMPLIANCE_INSTANCE_STATUSES.PENDING_REVIEW,
		icon: 'icons8-clock',
		iconStyle: { color: STOPLIGHT_COLORS.pureYellow },
	},
	{
		displayName: 'Approved',
		name: COMPLIANCE_INSTANCE_STATUSES.APPROVED,
		icon: 'icons8-approve',
		iconStyle: { color: STOPLIGHT_COLORS.pureGreen },
	},
	{
		displayName: 'Rejected',
		name: COMPLIANCE_INSTANCE_STATUSES.REJECTED,
		icon: 'icons8-cancel',
		iconStyle: { color: STOPLIGHT_COLORS.pureRed },
	},
];

// Supplier chevron pipeline — no Draft (suppliers never see drafts), and the
// waiting state reads as "Requested".
export const SUPPLIER_COMPLIANCE_PIPELINE_FILTER_ITEMS = [
	{
		displayName: 'Requested',
		name: COMPLIANCE_INSTANCE_STATUSES.WAITING_ON_SUPPLIER,
		icon: 'icons8-hourglass',
		iconStyle: { color: STOPLIGHT_COLORS.yellowRed },
	},
	{
		displayName: 'Pending Review',
		name: COMPLIANCE_INSTANCE_STATUSES.PENDING_REVIEW,
		icon: 'icons8-clock',
		iconStyle: { color: STOPLIGHT_COLORS.pureYellow },
	},
	{
		displayName: 'Approved',
		name: COMPLIANCE_INSTANCE_STATUSES.APPROVED,
		icon: 'icons8-approve',
		iconStyle: { color: STOPLIGHT_COLORS.pureGreen },
	},
	{
		displayName: 'Rejected',
		name: COMPLIANCE_INSTANCE_STATUSES.REJECTED,
		icon: 'icons8-cancel',
		iconStyle: { color: STOPLIGHT_COLORS.pureRed },
	},
];

export const COMPLIANCE_EXPIRY_STATUS_DISPLAY_NAMES = {
	[COMPLIANCE_EXPIRY_STATUSES.UP_TO_DATE]: 'Up to date',
	[COMPLIANCE_EXPIRY_STATUSES.EXPIRING_SOON]: 'Expiring soon',
	[COMPLIANCE_EXPIRY_STATUSES.EXPIRED]: 'Expired',
};

export const COMPLIANCE_EXPIRY_STATUS_COLORS = {
	[COMPLIANCE_EXPIRY_STATUSES.UP_TO_DATE]: STOPLIGHT_COLORS.pureGreen,
	[COMPLIANCE_EXPIRY_STATUSES.EXPIRING_SOON]: STOPLIGHT_COLORS.pureYellow,
	[COMPLIANCE_EXPIRY_STATUSES.EXPIRED]: STOPLIGHT_COLORS.pureRed,
};

export const COMPLIANCE_LIFECYCLE_DISPLAY_NAMES = {
	[COMPLIANCE_REQUEST_LIFECYCLES.DRAFT]: 'Draft',
	[COMPLIANCE_REQUEST_LIFECYCLES.PUBLISHED]: 'Published',
};

export const COMPLIANCE_LIFECYCLE_COLORS = {
	[COMPLIANCE_REQUEST_LIFECYCLES.DRAFT]: BLACKS_AND_WHITES.blackSecondary,
	[COMPLIANCE_REQUEST_LIFECYCLES.PUBLISHED]: STOPLIGHT_COLORS.pureGreen,
};

// Ordered status keys for the pipeline chip rows above the overview tables.
export const COMPLIANCE_OVERVIEW_CHIP_STATUS_ORDER = [
	COMPLIANCE_INSTANCE_STATUSES.WAITING_ON_SUPPLIER,
	COMPLIANCE_INSTANCE_STATUSES.PENDING_REVIEW,
	COMPLIANCE_INSTANCE_STATUSES.APPROVED,
	COMPLIANCE_INSTANCE_STATUSES.REJECTED,
];
